import { InfoItem } from "@/components/info-item";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectAnnotationView,
  selectIsEditable,
  selectSelectedEvent,
  setAnnotationData,
  setCurrentCamera,
} from "@/entities/annotation";
import { displayUUID, formatAbsoluteCoordinates } from "@/shared";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import { formatTimestamp, getHandSide } from "../../lib/helpers";
import { Button } from "@radix-ui/themes";
import { useCameras } from "../../hooks/useCameras";
import { usePlayer } from "@tezzro/player";

const ProximityEventDetails = () => {
  const player = usePlayer();
  const selectedEvent = useAppSelector(selectSelectedEvent);
  const dispatch = useAppDispatch();
  const annotationView = useAppSelector(selectAnnotationView);
  const isEditable = useAppSelector(selectIsEditable);
  const cameraData = useCameras();

  const onCreateProximityTimeRangesAnnotation = () => {
    if (selectedEvent?.type !== ANNOTATION_TYPES.PROXIMITY) {
      return null;
    }

    if (selectedEvent?.timestamp) {
      player.seek(selectedEvent?.timestamp, String(selectedEvent.cameraId));
      dispatch(setCurrentCamera(selectedEvent.cameraId));

      cameraData.setCameraModeFromCameraId(selectedEvent.cameraId);
    }

    const {
      orderId,
      journeyId,
      timestamp,
      pinCoordinates,
      calibrationId,
      gondolaName,
      gondolaId,
      planogramId,
      id: proximityId,
      handId,
    } = selectedEvent;

    dispatch(
      setAnnotationData({
        orderId,
        journeyId,
        timestamp,
        pinCoordinates,
        cameraId: String(selectedEvent.cameraId),
        calibrationId,
        gondolaName,
        gondolaId,
        planogramId,
        proximityId,
        handId,
        cameraImageWidth: selectedEvent.cameraImageWidth,
        cameraImageHeight: selectedEvent.cameraImageHeight,
      })
    );
  };

  if (!selectedEvent || selectedEvent.type !== ANNOTATION_TYPES.PROXIMITY) {
    return null;
  }

  const pinCoordinates = formatAbsoluteCoordinates(
    selectedEvent?.pinCoordinates.relativeX,
    selectedEvent?.pinCoordinates.relativeY,
    selectedEvent.cameraImageWidth,
    selectedEvent.cameraImageHeight
  );

  return (
    <>
      <InfoItem label="Event type" value="Proximity Annotation" />
      <InfoItem
        label="Journey ID"
        copyValue={selectedEvent?.journeyId}
        value={displayUUID(selectedEvent?.journeyId)}
      />
      <InfoItem
        label="Order ID"
        value={displayUUID(selectedEvent.orderId)}
        copyValue={selectedEvent.orderId}
      />
      <InfoItem
        label="Annotation ID"
        value={selectedEvent.id ? displayUUID(selectedEvent.id) : undefined}
        copyValue={selectedEvent.id || undefined}
      />
      <InfoItem
        label="Timestamp"
        value={
          selectedEvent?.timestamp && formatTimestamp(selectedEvent?.timestamp)
        }
        copyValue={selectedEvent?.timestamp}
      />
      <InfoItem label="Hand" value={getHandSide(selectedEvent.handId)} />
      <InfoItem label="Pin coordinates (x,y)" value={pinCoordinates} />
      <InfoItem
        label="Camera image dimensions"
        value={`${selectedEvent?.cameraImageWidth}x${selectedEvent?.cameraImageHeight}`}
      />
      <InfoItem label="Camera ID" value={selectedEvent?.cameraId} />
      <InfoItem label="Calibration ID" value={selectedEvent?.calibrationId} />
      <InfoItem label="Gondola name" value={selectedEvent?.gondolaName} />
      <InfoItem label="Gondola ID" value={selectedEvent?.gondolaId} />
      <InfoItem label="Planogram ID" value={selectedEvent?.planogramId} />
      <InfoItem label="Note" value={selectedEvent.notes} />
      {isEditable &&
        annotationView === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES && (
          <Button
            size="1"
            color="gray"
            highContrast
            type="submit"
            className="mt-4"
            onClick={onCreateProximityTimeRangesAnnotation}
          >
            Create Proximity Time Range Annotation
          </Button>
        )}
    </>
  );
};

export default ProximityEventDetails;
